import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faMoneyBillWave,
  faHome,
  faTag,
  faHeartCrack, // For delete favorite
  faSpinner, // For loading
  faExclamationCircle, // For error
  faFaceSadTear, // For empty state
  faChevronLeft, // For back button
} from "@fortawesome/free-solid-svg-icons";

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = userDataString ? JSON.parse(userDataString) : null;
  const userId = userData?.unique;

  useEffect(() => {
    const fetchFavoritesAndRentals = async () => {
      setLoading(true);
      setError(null); // Clear previous errors

      if (!token || !userId) {
        setError("Authentication required. Please log in.");
        setLoading(false);
        toast.error("Authentication required. Please log in.");
        return;
      }

      try {
        // Fetch favorites
        const favoritesResponse = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/favorite/users/${userId}/favorites`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const fetchedFavorites = favoritesResponse.data.response;
        setFavorites(fetchedFavorites);

        // If no favorites, stop loading and return
        if (!fetchedFavorites || fetchedFavorites.length === 0) {
          setRentals([]);
          setLoading(false);
          return;
        }

        // Fetch rental details for each favorite concurrently
        const rentalPromises = fetchedFavorites.map((favorite) =>
          axios.get(
            `https://backend.inyumba.co.ke/iNyumba/admin/rentals/specific/${favorite.rental_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ).then(res => res.data.response)
          .catch(err => {
            console.error(`Error fetching rental ${favorite.rental_id}:`, err);
            return null; // Return null for failed fetches to filter out later
          })
        );

        const rentalResponses = await Promise.all(rentalPromises);
        // Filter out any null responses (failed fetches)
        const validRentals = rentalResponses.filter(rental => rental !== null);
        setRentals(validRentals);
      } catch (err) {
        console.error("Error fetching favorites or rentals:", err);
        setError("Failed to load your favorite rentals. Please try again.");
        toast.error(err.response?.data?.response || "Failed to load favorites.");
      } finally {
        setLoading(false);
      }
    };

    fetchFavoritesAndRentals();
  }, [userId, token]);

  const handleDeleteFavorite = async (rentalId, event) => {
    event.stopPropagation(); // Prevent card onClick from firing

    if (!userId || !token) {
      toast.error("Authentication required to delete favorite.");
      return;
    }

    // Find the favorite with the corresponding rental_id
    const favoriteToDelete = favorites.find(
      (favorite) => favorite.rental_id === rentalId
    );

    if (!favoriteToDelete) {
      toast.error("Favorite not found for deletion.");
      console.error("Favorite not found for deletion");
      return;
    }

    try {
      await axios.delete(
        `https://backend.inyumba.co.ke/iNyumba/favorite/users/${userId}/favorites/${favoriteToDelete.rental_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Optimistically update UI: remove the deleted rental from the rentals state
      setRentals(rentals.filter((rental) => rental.rental_id !== rentalId));
      // Also update the raw favorites list if needed, though rentals is what's rendered
      setFavorites(favorites.filter((fav) => fav.rental_id !== rentalId));
      toast.success("Favorite successfully removed!");
    } catch (error) {
      console.error("Error deleting favorite:", error);
      toast.error(error.response?.data?.response || "Failed to delete favorite.");
    }
  };

  const handleViewRental = (rentalId) => {
    localStorage.setItem("specificRentalId", rentalId);
    navigate("/user/specificrental");
  };

  // --- Loading State UI ---
  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
          <div className="flex flex-col items-center text-gray-600 p-8 rounded-lg bg-white shadow-lg border border-blue-200 animate-pulse">
            <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-4 text-indigo-500" />
            <p className="text-xl font-medium">Loading your favorite rentals...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // --- Error State UI ---
  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 p-6">
          <div className="flex flex-col items-center text-red-700 p-8 rounded-lg bg-white shadow-lg border border-red-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Favorites</h2>
            <p className="text-center mb-4 text-lg">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
            >
              Go Back
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-6 sm:p-8 lg:p-10">
        
        {/* Back Button */}
        <div className="mb-8 animate-fade-in-down">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-800 rounded-full shadow-md hover:bg-gray-100 transition-colors transform hover:-translate-x-1"
            aria-label="Back to Previous Page"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            Back
          </button>
        </div>

        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-10 animate-fade-in-up">
          Your Favorite Rentals
        </h1>

        {rentals.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-xl max-w-2xl mx-auto text-gray-700 animate-fade-in">
            <FontAwesomeIcon icon={faFaceSadTear} className="text-6xl text-indigo-400 mb-6" />
            <h2 className="text-2xl font-semibold mb-3">No Favorites Yet!</h2>
            <p className="text-lg text-center mb-6">
              It looks like you haven't added any rentals to your favorites.
              Start exploring the marketplace to find your perfect home!
            </p>
            <button
              onClick={() => navigate("/user/propertymarketplace")} // Assuming marketplace path
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105"
            >
              Explore Rentals
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
            {rentals.map((rental) => (
              <div
                key={rental.rental_id}
                className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl flex flex-col"
                onClick={() => handleViewRental(rental.rental_id)}
              >
                <div className="relative overflow-hidden rounded-xl mb-4 h-48">
                  <img
                    src={rental.photo}
                    alt={`${rental.house_type} - ${rental.house_number}`}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div
                    className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      rental.status === "OPEN" // Use "OPEN" as per your backend
                        ? "bg-green-500 text-white"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {rental.status}
                  </div>
                </div>

                <h2 className="text-xl font-extrabold text-gray-900 mb-2 leading-tight">
                  {rental.house_type} - House No. {rental.house_number}
                </h2>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-indigo-400" />
                  {rental.property_name} ({rental.property_location})
                </p>

                <div className="flex items-center text-lg font-bold text-gray-800 mb-3">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-500" />
                  KES {Number(rental.rent_price).toLocaleString()}
                  <span className="text-sm font-medium text-gray-500 ml-1">/month</span>
                </div>

                <p className="text-gray-700 text-sm mb-4 flex-grow">
                  {rental.description}
                </p>

                <div className="mt-auto">
                  <button
                    onClick={(event) => handleDeleteFavorite(rental.rental_id, event)}
                    className="w-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                  >
                    <FontAwesomeIcon icon={faHeartCrack} className="mr-2" />
                    Remove from Favorites
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

export default FavoritesPage;